<template>
  <div class="admin-layout">
    <div class="sidebar-overlay" :class="{ visible: sidebarVisible }" @click="closeSidebar"></div>

    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed, mobileOpen: sidebarVisible }">
      <div class="sidebar-header">
        <button class="trigger-btn" @click="toggleSidebar">
          <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </button>
        <router-link to="/admin" class="brand" v-show="!sidebarCollapsed">
          <div class="brand-icon">
            <svg viewBox="0 0 120 120" fill="none">
              <rect width="120" height="120" rx="24" fill="#3b82f6"/>
              <g stroke="white" stroke-width="7.5" stroke-linecap="round" stroke-linejoin="round" fill="none">
                <path d="M60 30.2c-8-7.1-19.5-11.5-31-11.5-5.3 0-10.3.9-15 2.6v71.3c4.7-1.7 9.7-2.6 15-2.6 11.5 0 23 4.4 31 11.5"/>
                <path d="M60 30.2c8-7.1 19.5-11.5 31-11.5 5.3 0 10.3.9 15 2.6v71.3c-4.7-1.7-9.7-2.6-15-2.6-11.5 0-23 4.4-31 11.5"/>
                <path d="M60 30.2v71.3"/>
              </g>
            </svg>
          </div>
          <span class="brand-text">{{ $t('adminLayout.brand') }}</span>
        </router-link>
      </div>

      <nav class="sidebar-nav">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ active: isActive(item.path), collapsed: sidebarCollapsed }"
        >
          <div class="nav-icon-wrapper">
            <i :class="item.icon"></i>
          </div>
          <span class="nav-label" v-show="!sidebarCollapsed">{{ $t('adminLayout.' + item.key) }}</span>
          <div class="nav-tooltip" v-show="sidebarCollapsed">{{ $t('adminLayout.' + item.key) }}</div>
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <div class="user-profile">
          <el-avatar :size="sidebarCollapsed ? 34 : 34" class="user-avatar">
            {{ userName.charAt(0).toUpperCase() }}
          </el-avatar>
          <div class="user-info" v-show="!sidebarCollapsed">
            <div class="user-name">{{ userName }}</div>
            <div class="user-role">{{ roleLabel }}</div>
          </div>
        </div>
      </div>
    </aside>

    <div class="main-wrapper">
      <header class="topbar">
        <div class="topbar-left">
          <button class="mobile-menu-btn" @click="openSidebar">
            <i class="el-icon-s-unfold"></i>
          </button>
          <h2 class="page-title">{{ pageTitle }}</h2>
        </div>
        <div class="topbar-right">
          <div class="search-box">
            <i class="el-icon-search search-icon"></i>
            <input
              type="text"
              class="search-input"
              :placeholder="$t('adminLayout.searchPlaceholder')"
              v-model="searchQuery"
            />
          </div>

          <el-tooltip :content="$t('adminLayout.notifications')" placement="bottom">
            <el-badge :value="3" class="notif-btn">
              <i class="el-icon-bell"></i>
            </el-badge>
          </el-tooltip>

          <LanguageSwitcher />

          <el-dropdown @command="handleCommand" trigger="click" class="user-dropdown">
            <div class="user-trigger">
              <el-avatar :size="32" class="topbar-avatar">
                {{ userName.charAt(0).toUpperCase() }}
              </el-avatar>
              <span class="user-name-label">{{ userName }}</span>
              <i class="el-icon-arrow-down"></i>
            </div>
            <el-dropdown-menu slot="dropdown" class="dropdown-menu">
              <el-dropdown-item command="profile">
                <i class="el-icon-user"></i> {{ $t('adminLayout.profileSettings') }}
              </el-dropdown-item>
              <el-dropdown-item command="settings">
                <i class="el-icon-setting"></i> {{ $t('adminLayout.systemSettings') }}
              </el-dropdown-item>
              <el-dropdown-item command="front">
                <i class="el-icon-s-home"></i> {{ $t('adminLayout.backToFront') }}
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <i class="el-icon-switch-button"></i> {{ $t('adminLayout.logout') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <main class="content-area">
        <router-view/>
      </main>
    </div>
  </div>
</template>

<script>
import { roleLabels, hasRole } from '@/utils/permission'

export default {
  name: 'AdminLayout',
  components: {
    LanguageSwitcher: () => import('@/components/LanguageSwitcher.vue')
  },
  data() {
    return {
      userName: 'Admin',
      searchQuery: '',
      sidebarCollapsed: false,
      sidebarVisible: false,
      allMenuItems: [
        { path: '/admin', key: 'dashboard', icon: 'el-icon-s-home', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
        { path: '/admin/users', key: 'users', icon: 'el-icon-user', roles: ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'] },
        { path: '/admin/roles', key: 'roles', icon: 'el-icon-s-custom', roles: ['ROLE_SUPER_ADMIN'] },
        { path: '/admin/permissions', key: 'permissions', icon: 'el-icon-menu', roles: ['ROLE_SUPER_ADMIN'] },
        { path: '/admin/texts', key: 'texts', icon: 'el-icon-document', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] },
        { path: '/admin/settings', key: 'settings', icon: 'el-icon-setting', roles: ['ROLE_ADMIN', 'ROLE_MODERATOR', 'ROLE_SUPER_ADMIN'] }
      ]
    }
  },
  computed: {
    menuItems() {
      return this.allMenuItems.filter(item =>
        !item.roles || item.roles.some(r => hasRole(r))
      )
    },
    roleLabel() {
      return roleLabels()
    },
    pageTitle() {
      const route = this.$route.path
      const item = this.menuItems.find(m => m.path === route)
      return item ? this.$t('adminLayout.' + item.key) : this.$t('adminLayout.dashboard')
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadUser()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    loadUser() {
      const userInfo = localStorage.getItem('user')
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo)
          this.userName = user.nickname || user.name || 'Admin'
        } catch {
          this.userName = 'Admin'
        }
      }
    },
    checkMobile() {
      const mobile = window.innerWidth <= 768
      if (mobile) {
        this.sidebarCollapsed = true
        this.sidebarVisible = false
      }
    },
    toggleSidebar() {
      if (window.innerWidth <= 768) {
        this.sidebarVisible = !this.sidebarVisible
      } else {
        this.sidebarCollapsed = !this.sidebarCollapsed
      }
    },
    openSidebar() {
      this.sidebarVisible = true
    },
    closeSidebar() {
      this.sidebarVisible = false
    },
    isActive(path) {
      return this.$route.path === path ||
             (path !== '/admin' && this.$route.path.startsWith(path))
    },
    handleCommand(command) {
      switch (command) {
        case 'logout':
          this.handleLogout()
          break
        case 'profile':
          this.$message.info(this.$t('adminLayout.profileDev'))
          break
        case 'settings':
          this.$router.push('/admin/settings')
          break
        case 'front':
          window.open('/', '_blank')
          break
      }
    },
    async handleLogout() {
      try {
        await this.$confirm(this.$t('adminLayout.logoutConfirm'), this.$t('adminLayout.logoutConfirmTitle'), {
          confirmButtonText: this.$t('adminLayout.logoutBtn'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        await this.$store.dispatch('logout')
        this.$message.success(this.$t('common.logoutSuccess'))
        this.$router.push('/admin/login')
      } catch {
        // User cancelled
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.admin-layout {
  display: flex;
  height: 100vh;
  background: $bg-body;
  overflow: hidden;
}

// ═══════════════════════════════════════════
//  Sidebar Overlay (mobile)
// ═══════════════════════════════════════════
.sidebar-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: $bg-overlay;
  z-index: $z-modal-backdrop;
  opacity: 0;
  transition: opacity 0.3s;

  &.visible {
    display: block;
    opacity: 1;
  }
}

// ═══════════════════════════════════════════
//  Sidebar
// ═══════════════════════════════════════════
.sidebar {
  width: 260px;
  background: $white;
  display: flex;
  flex-direction: column;
  border-right: 1px solid $border-light;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  z-index: $z-sidebar;

  &.collapsed {
    width: 68px;
  }

  // ─── Header ─────────────────────────────
  .sidebar-header {
    height: 64px;
    padding: 0 14px;
    display: flex;
    align-items: center;
    gap: 0;
    border-bottom: 1px solid $border-light;
    flex-shrink: 0;

    .trigger-btn {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      border-radius: $radius-sm;
      color: $text-tertiary;
      cursor: pointer;
      transition: $transition-fast;
      flex-shrink: 0;
      margin-right: 4px;

      &:hover {
        background: $gray-100;
        color: $text-primary;
      }

      i {
        font-size: 20px;
        transition: transform 0.25s;
      }
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
      text-decoration: none;
      min-width: 0;
      overflow: hidden;

      .brand-icon {
        width: 34px;
        height: 34px;
        flex-shrink: 0;

        svg {
          display: block;
          width: 100%;
          height: 100%;
        }
      }

      .brand-text {
        font-size: $font-lg;
        font-weight: $font-weight-bold;
        @include gradient-text;
        white-space: nowrap;
      }
    }
  }

  // ─── Navigation ──────────────────────────
  .sidebar-nav {
    flex: 1;
    padding: 12px 10px;
    overflow-y: auto;
    overflow-x: hidden;
    @include scrollbar;

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 14px;
      margin-bottom: 2px;
      border-radius: $radius-md;
      color: $text-secondary;
      text-decoration: none;
      font-size: $font-base;
      font-weight: $font-weight-medium;
      transition: $transition-fast;
      cursor: pointer;
      position: relative;
      white-space: nowrap;

      .nav-icon-wrapper {
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          font-size: 20px;
          color: $text-tertiary;
          transition: $transition-fast;
        }
      }

      .nav-label {
        transition: opacity 0.2s;
      }

      .nav-tooltip {
        position: fixed;
        left: 76px;
        background: $gray-800;
        color: $white;
        padding: 6px 12px;
        border-radius: $radius-sm;
        font-size: $font-xs;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: $transition-fast;
        z-index: $z-tooltip;

        &::before {
          content: '';
          position: absolute;
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border: 5px solid transparent;
          border-right-color: $gray-800;
        }
      }

      &:hover {
        background: $primary-lightest;

        .nav-icon-wrapper i {
          color: $primary;
        }

        .nav-tooltip {
          opacity: 1;
        }
      }

      &.active {
        background: $primary;
        color: $white;
        box-shadow: 0 2px 8px rgba($primary, 0.25);

        .nav-icon-wrapper i {
          color: $white;
        }

        &:hover {
          background: $primary-dark;
        }
      }

      &.collapsed {
        justify-content: center;
        padding: 10px 0;

        .nav-icon-wrapper {
          width: auto;
          height: auto;
        }
      }
    }
  }

  // ─── Footer ─────────────────────────────
  .sidebar-footer {
    border-top: 1px solid $border-light;
    padding: 12px 14px;
    flex-shrink: 0;

    .user-profile {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px;
      border-radius: $radius-md;
      cursor: default;

      .user-avatar {
        background: $primary;
        color: $white;
        font-weight: $font-weight-semibold;
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba($primary, 0.3);
      }

      .user-info {
        min-width: 0;

        .user-name {
          font-weight: $font-weight-semibold;
          color: $text-primary;
          font-size: $font-sm;
          @include truncate;
        }

        .user-role {
          font-size: $font-xs;
          color: $text-tertiary;
          margin-top: 1px;
        }
      }
    }
  }
}

// ═══════════════════════════════════════════
//  Main Wrapper
// ═══════════════════════════════════════════
.main-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

// ═══════════════════════════════════════════
//  Topbar
// ═══════════════════════════════════════════
.topbar {
  height: 64px;
  background: $white;
  border-bottom: 1px solid $border-light;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  flex-shrink: 0;

  .mobile-menu-btn {
    display: none;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: $radius-sm;
    color: $text-tertiary;
    cursor: pointer;

    &:hover {
      background: $gray-100;
      color: $text-primary;
    }

    i {
      font-size: 20px;
    }
  }

  .topbar-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .page-title {
      font-size: $font-xl;
      font-weight: $font-weight-bold;
      color: $text-primary;
      margin: 0;
      letter-spacing: -0.3px;
    }
  }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: 8px;

    .search-box {
      position: relative;
      width: 260px;

      .search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: $text-tertiary;
        font-size: 15px;
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        height: 36px;
        padding: 0 14px 0 36px;
        border: 1px solid transparent;
        border-radius: $radius-full;
        background: $gray-50;
        color: $text-primary;
        font-size: $font-sm;
        outline: none;
        transition: $transition-fast;

        &::placeholder {
          color: $text-placeholder;
        }

        &:focus {
          background: $white;
          border-color: $primary-light;
          box-shadow: 0 0 0 3px rgba($primary, 0.1);
        }
      }
    }

    .notif-btn {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: $radius-sm;
      cursor: pointer;
      transition: $transition-fast;

      &:hover {
        background: $gray-50;
      }

      i {
        font-size: 20px;
        color: $text-tertiary;
        transition: $transition-fast;
      }

      &:hover i {
        color: $text-primary;
      }

      ::v-deep .el-badge__content {
        background: $error !important;
        border: 2px solid $white !important;
      }
    }

    .user-dropdown {
      margin-left: 4px;
    }

    .user-trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px 4px 4px;
      border-radius: $radius-full;
      cursor: pointer;
      transition: $transition-fast;

      &:hover {
        background: $gray-50;
      }

      .topbar-avatar {
        background: $primary;
        color: $white;
        font-weight: $font-weight-semibold;
      }

      .user-name-label {
        font-size: $font-sm;
        font-weight: $font-weight-medium;
        color: $text-primary;
        max-width: 80px;
        @include truncate;
      }

      .el-icon-arrow-down {
        font-size: 12px;
        color: $text-tertiary;
      }
    }
  }
}

// ═══════════════════════════════════════════
//  Content Area
// ═══════════════════════════════════════════
.content-area {
  flex: 1;
  padding: 28px;
  overflow-y: auto;
  @include scrollbar;
}

// ═══════════════════════════════════════════
//  Dropdown Menu Override
// ═══════════════════════════════════════════
::v-deep .dropdown-menu {
  border: none;
  box-shadow: $shadow-lg;
  border-radius: $radius-md;
  padding: 6px;
  min-width: 180px;

  .el-dropdown-menu__item {
    border-radius: $radius-sm;
    padding: 8px 12px;
    font-size: $font-sm;
    color: $text-secondary;

    i {
      margin-right: 8px;
      font-size: 16px;
      color: $text-tertiary;
    }

    &:hover {
      background: $primary-lightest;
      color: $primary;

      i {
        color: $primary;
      }
    }

    &.is-disabled {
      opacity: 0.4;
    }
  }

  .el-dropdown-menu__item--divided {
    border-top-color: $border-light;
    margin-top: 4px;
    padding-top: 8px;
  }
}

// ═══════════════════════════════════════════
//  Responsive
// ═══════════════════════════════════════════
@media (max-width: 768px) {
  .sidebar-overlay.visible {
    display: block;
  }

  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: $z-modal;
    box-shadow: $shadow-xl;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 280px;

    &.mobileOpen {
      transform: translateX(0);
    }

    &.collapsed {
      width: 280px;
    }

    .sidebar-header {
      .trigger-btn {
        display: none;
      }
    }

    .nav-item.collapsed {
      justify-content: flex-start;
      padding: 10px 14px;
    }
  }

  .topbar {
    padding: 0 16px;

    .mobile-menu-btn {
      display: flex;
    }

    .search-box {
      display: none;
    }

    .user-trigger .user-name-label {
      display: none;
    }

    .page-title {
      font-size: $font-lg;
    }
  }

  .content-area {
    padding: 20px;
  }
}
</style>
